import type {
  CharacterIndexProgressEvidenceKind,
  CharacterIndexReaderResumeTarget,
} from './characterIndexProgress'
import { buildLedgerStudioPath } from './ledgerStudioRoute'

export interface BuildReaderStudioPathInput {
  target: CharacterIndexReaderResumeTarget
  returnTo?: string
}

export interface ParsedReaderStudioPath {
  target: CharacterIndexReaderResumeTarget
  returnTo?: string
}

export interface BuildEditorialStudioPathInput {
  bookId: string
  chapterId: string
  readerResume?: CharacterIndexReaderResumeTarget
  returnTo?: string
}

export interface ReaderScopeHydrationState {
  readerTarget?: CharacterIndexReaderResumeTarget | null
  scopedWitnessCount: number
}

const evidenceKinds = new Set<CharacterIndexProgressEvidenceKind>([
  'cast',
  'coreference',
  'focalization',
  'characterization',
])

const isSafeInternalPath = (value: string): boolean => (
  value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
)

const encodePathPart = (value: string): string => encodeURIComponent(value)

const decodePathPart = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return ''
  }
}

export const buildReaderStudioPath = ({
  target,
  returnTo,
}: BuildReaderStudioPathInput): string => {
  const pathname = [
    '',
    'books',
    encodePathPart(target.bookId),
    'chapters',
    encodePathPart(target.chapterId),
    'reader',
    encodePathPart(target.witnessId),
  ].join('/')
  const search = new URLSearchParams({ evidence: target.evidenceKind })
  if (target.rowId) search.set('row', target.rowId)
  if (target.unitLabel) search.set('unit', target.unitLabel)
  if (target.characterId) search.set('character', target.characterId)
  if (target.evidenceId) search.set('item', target.evidenceId)
  if (returnTo && isSafeInternalPath(returnTo)) search.set('return', returnTo)
  return `${pathname}?${search.toString()}`
}

export const buildEditorialStudioPath = ({
  bookId,
  chapterId,
  readerResume,
  returnTo,
}: BuildEditorialStudioPathInput): string => (
  readerResume
    ? buildReaderStudioPath({ target: readerResume, returnTo })
    : buildLedgerStudioPath({ bookId, chapterId })
)

/**
 * A first-class Reader route is authoritative while the Ledger derives its
 * initial witness scope. The scope is empty for one render before the stored
 * scope selection is restored; that transient state must not close Reader.
 */
export const shouldPreserveReaderIntentDuringScopeHydration = ({
  readerTarget,
  scopedWitnessCount,
}: ReaderScopeHydrationState): boolean => Boolean(readerTarget) && scopedWitnessCount === 0

export const parseReaderStudioPath = (
  pathname: string,
  searchValue: string,
): ParsedReaderStudioPath | null => {
  const segments = (pathname || '').split('/').filter(Boolean)
  if (
    segments.length !== 6
    || segments[0] !== 'books'
    || segments[2] !== 'chapters'
    || segments[4] !== 'reader'
  ) {
    return null
  }

  const bookId = decodePathPart(segments[1])
  const chapterId = decodePathPart(segments[3])
  const witnessId = decodePathPart(segments[5])
  const search = new URLSearchParams(searchValue)
  const evidenceKind = search.get('evidence') || 'cast'
  if (!bookId || !chapterId || !witnessId || !evidenceKinds.has(evidenceKind as CharacterIndexProgressEvidenceKind)) {
    return null
  }

  const target: CharacterIndexReaderResumeTarget = {
    kind: 'reader-character-index',
    bookId,
    chapterId,
    witnessId,
    evidenceKind: evidenceKind as CharacterIndexProgressEvidenceKind,
    ...(search.get('row') ? { rowId: search.get('row')! } : {}),
    ...(search.get('unit') ? { unitLabel: search.get('unit')! } : {}),
    ...(search.get('character') ? { characterId: search.get('character')! } : {}),
    ...(search.get('item') ? { evidenceId: search.get('item')! } : {}),
  }
  const returnTo = search.get('return') || ''
  return {
    target,
    ...(returnTo && isSafeInternalPath(returnTo) ? { returnTo } : {}),
  }
}
